import { useId, useMemo, useState } from 'react'
import { CircleNotch, Warning } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { num } from '@/lib/format'
import type { AckPlan } from './ack-plan'
import type { BulkAckLoadedPo, BulkAckQtyDraftByKey } from './bulk-ack-model'
import { planForLoadedPo, summarizeBulkAck } from './bulk-ack-model'

/**
 * Bulk consent step before calling Vision once per PO.
 * Mirrors {@link AckConfirmDialog}: zero-qty Accepts and validation Rejects are listed.
 */
export function BulkAckConfirmDialog({
  open,
  onOpenChange,
  loaded,
  drafts,
  pending,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  loaded: readonly BulkAckLoadedPo[]
  drafts: BulkAckQtyDraftByKey
  pending: boolean
  onConfirm: () => void
}) {
  const [typed, setTyped] = useState('')
  const fieldId = useId()
  const confirmId = `${fieldId}-confirm`
  const confirmHintId = `${fieldId}-confirm-hint`
  const summary = useMemo(() => summarizeBulkAck(loaded, drafts), [loaded, drafts])
  const plans = useMemo(
    () => loaded.map(entry => ({ entry, plan: planForLoadedPo(entry, drafts) })),
    [loaded, drafts],
  )
  const allRejected = plans.length > 0 && plans.every(({ plan }) => plan.allRejected)
  const typeToConfirm = allRejected
  const typedMatches = typed.trim().toUpperCase() === 'ACKNOWLEDGE'

  const zeroLines = plans.flatMap(({ entry, plan }) =>
    plan.lines
      .filter(line => line.acknowledgedQuantity === 0)
      .map(line => {
        const item = entry.order.items.find(
          row => row.itemSequenceNumber === line.itemSequenceNumber,
        )
        return {
          po: entry.order.externalOrderNumber,
          label: item
            ? `#${item.itemSequenceNumber} ${item.displayName || item.channelSku}`
            : `#${line.itemSequenceNumber}`,
        }
      }),
  )
  const rejectedLines = plans.flatMap(({ entry, plan }) =>
    plan.rejected.map(line => ({
      po: entry.order.externalOrderNumber,
      label: line.label,
      units: line.units,
      checks: line.checks,
    })),
  )

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) setTyped('')
        onOpenChange(next)
      }}
    >
      <DialogContent data-testid="bulk-ack-confirm" className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[color:var(--crit)]">
            <Warning className="size-4" />
            Acknowledge {num(summary.poCount)} POs?
          </DialogTitle>
          <DialogDescription>
            Vision is called once per PO. Validation-failed lines go to Amazon as Rejected at full
            ordered qty. Accepted quantity 0 is sent as Accepted with quantity 0.
          </DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-2 gap-3 rounded-xl bg-[color:var(--crit-soft)]/40 px-3 py-2.5">
          <div>
            <dt className="text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
              Validation rejected
            </dt>
            <dd className="text-[15px] font-semibold tabular-nums text-[color:var(--crit)]">
              {num(summary.validationRejectedLines)} lines
            </dd>
          </div>
          <div>
            <dt className="text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
              Zero accepted
            </dt>
            <dd className="text-[15px] font-semibold tabular-nums text-[color:var(--foreground)]">
              {num(summary.zeroQtyLines)} lines
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]">
              Units accepted
            </dt>
            <dd className="text-[15px] font-semibold tabular-nums text-[color:var(--foreground)]">
              {num(summary.acceptedUnits)}
            </dd>
          </div>
        </dl>

        {rejectedLines.length > 0 ? (
          <ul className="flex max-h-40 flex-col gap-2 overflow-y-auto">
            {rejectedLines.map(line => (
              <li
                key={`${line.po}-${line.label}`}
                className="rounded-xl bg-[color:var(--crit-soft)]/40 px-3 py-2 text-[12.5px]"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 break-words font-medium">
                    {line.po} · {line.label}
                  </span>
                  <span className="shrink-0 font-semibold tabular-nums text-[color:var(--crit)]">
                    {num(line.units)} units
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {zeroLines.length > 0 ? (
          <ul className="flex max-h-32 flex-col gap-1 overflow-y-auto text-[12px] text-[color:var(--muted-foreground)]">
            {zeroLines.map(line => (
              <li key={`${line.po}-${line.label}`}>
                {line.po} · {line.label} · accepted 0
              </li>
            ))}
          </ul>
        ) : null}

        {typeToConfirm ? (
          <div className="flex flex-col gap-1.5">
            <Label id={confirmHintId} htmlFor={confirmId}>
              Type ACKNOWLEDGE to confirm rejecting every line
            </Label>
            <Input
              id={confirmId}
              value={typed}
              onChange={event => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Keep editing
          </Button>
          <Button
            type="button"
            variant="destructive"
            data-testid="bulk-ack-confirm-submit"
            disabled={pending || (typeToConfirm && !typedMatches)}
            aria-describedby={typeToConfirm ? confirmHintId : undefined}
            onClick={onConfirm}
          >
            {pending ? <CircleNotch className="size-3.5 animate-spin" /> : null}
            Submit {num(summary.poCount)} POs
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function plansForBulk(
  loaded: readonly BulkAckLoadedPo[],
  drafts: BulkAckQtyDraftByKey,
): AckPlan[] {
  return loaded.map(entry => planForLoadedPo(entry, drafts))
}
